import { useState } from "react";
import { Link2, Loader2, MapPin, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { CATEGORIES, CAT_RGB, toLocal, tzShort, deviceTz, whenLabel } from "@/lib/calendar";
import { Field } from "@/components/common";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function initial(e, tz, day) {
  if (!e) return { title: "", category: "Family", all_day: false, sd: day, st: "18:00", ed: day, et: "19:00", location: "", notes: "", member_keys: [], freq: "", days: [], until: "", reminder: "" };
  const s = e.all_day ? `${e.start}T00:00` : toLocal(e.start, tz);
  const en = e.all_day ? `${e.end}T00:00` : toLocal(e.end, tz);
  return { title: e.title, category: e.category, all_day: e.all_day, sd: s.slice(0, 10), st: s.slice(11), ed: en.slice(0, 10), et: en.slice(11), location: e.location || "", notes: e.notes || "",
    member_keys: e.member_keys, freq: e.repeat?.freq || "", days: e.repeat?.days || [], until: e.repeat?.until || "", reminder: e.reminder || "" };
}

const shift = (local, ms) => new Date(Date.parse(`${local}:00Z`) + ms).toISOString().slice(0, 16);

function body(f, ev, scope) {
  let start = f.all_day ? f.sd : `${f.sd}T${f.st}`;
  let end = f.all_day ? f.ed : `${f.ed}T${f.et}`;
  if (ev?.repeat && scope === "all" && ev.series_start) {
    // keep the series' first date; apply the new time and duration
    const dur = f.all_day ? 0 : Date.parse(`${end}:00Z`) - Date.parse(`${start}:00Z`);
    start = f.all_day ? ev.series_start.slice(0, 10) : `${ev.series_start.slice(0, 10)}T${f.st}`;
    end = f.all_day ? shift(`${ev.series_start.slice(0, 10)}T00:00`, Date.parse(`${f.ed}T00:00Z`) - Date.parse(`${f.sd}T00:00Z`)).slice(0, 10) : shift(start, dur);
  }
  return { title: f.title, category: f.category, all_day: f.all_day, start, end, location: f.location, notes: f.notes, member_keys: f.member_keys,
    repeat: f.freq ? { freq: f.freq, days: f.freq === "weekly" ? f.days : null, until: f.until || null } : null, reminder: f.reminder || null };
}

function Chips({ options, value, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o.value);
        return <button type="button" key={o.value} onClick={() => onToggle(o.value)} data-testid={`${testid}-${o.tid || o.value}`} aria-pressed={on}
          className={`min-h-[44px] rounded-full border px-3 text-sm font-semibold transition-colors duration-150 ${on ? "border-transparent text-white" : "border-q-line text-q-muted"}`}
          style={on ? { background: "rgb(var(--glow-calendar))" } : undefined}>{o.label}</button>;
      })}
    </div>
  );
}

export function EventForm({ hubId, meta, ev, day, onClose, onSaved }) {
  const tz = meta.timezone;
  const [f, setF] = useState(() => initial(ev, tz, day));
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [askScope, setAskScope] = useState(false);
  const set = (k) => (e) => { setF((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e })); setErrs({}); };
  const toggle = (k) => (v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));
  const save = async (scope) => {
    const local = {};
    if (!f.title.trim()) local.title = "Give the event a title";
    if ((f.all_day ? f.ed < f.sd : `${f.ed}T${f.et}` < `${f.sd}T${f.st}`)) local.end = "The end can't be before the start";
    if (Object.keys(local).length) return setErrs(local);
    if (ev?.repeat && !scope) return setAskScope(true);
    setBusy(true);
    try {
      const b = body(f, ev, scope);
      if (ev) await api.patch(`/organizations/${hubId}/calendar/events/${ev.event_id}`, b, { params: { scope: scope || "all", occurrence: ev.occurrence } });
      else await api.post(`/organizations/${hubId}/calendar/events`, b);
      toast.success(ev ? "Event updated" : "Event added");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.field) setErrs({ [d.field]: d.message }); else toast.error(errMsg(e));
    } finally { setBusy(false); setAskScope(false); }
  };
  const differs = deviceTz() !== tz;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="event-form">
        <DialogHeader><DialogTitle>{ev ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
        <form className="space-y-4" noValidate onSubmit={(e) => { e.preventDefault(); save(); }}>
          <Field label="Title" htmlFor="ev-title" error={errs.title} errorTestid="event-title-error">
            <input id="ev-title" className="q-input" value={f.title} onChange={set("title")} aria-invalid={!!errs.title} data-testid="event-title-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" htmlFor="ev-cat"><select id="ev-cat" className="q-input" value={f.category} onChange={set("category")} data-testid="event-category-select">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <label className="mt-6 flex min-h-[44px] items-center gap-2 text-sm font-semibold text-q-text"><input type="checkbox" checked={f.all_day} onChange={set("all_day")} className="h-5 w-5" data-testid="event-allday-checkbox" /> All day</label>
          </div>
          {differs && !f.all_day && <p className="text-xs text-q-faint" data-testid="event-tz-note">Times are in the family's time zone ({tzShort(tz)}).</p>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts" htmlFor="ev-sd"><input id="ev-sd" type="date" className="q-input" value={f.sd} onChange={(e) => { const v = e.target.value; setF((p) => ({ ...p, sd: v, ed: p.ed < v ? v : p.ed })); setErrs({}); }} data-testid="event-start-date" /></Field>
            {!f.all_day && <Field label="Start time" htmlFor="ev-st"><input id="ev-st" type="time" className="q-input" value={f.st} onChange={set("st")} data-testid="event-start-time" /></Field>}
            <Field label="Ends" htmlFor="ev-ed" error={errs.end} errorTestid="event-end-error"><input id="ev-ed" type="date" className="q-input" value={f.ed} onChange={set("ed")} aria-invalid={!!errs.end} data-testid="event-end-date" /></Field>
            {!f.all_day && <Field label="End time" htmlFor="ev-et"><input id="ev-et" type="time" className="q-input" value={f.et} onChange={set("et")} data-testid="event-end-time" /></Field>}
          </div>
          <Field label="Location" htmlFor="ev-loc"><input id="ev-loc" className="q-input" value={f.location} onChange={set("location")} data-testid="event-location-input" /></Field>
          <div><p className="q-label">Who's going</p><Chips options={meta.people.map((p) => ({ value: p.key, label: p.name.split(" ")[0], tid: p.name.split(" ")[0].toLowerCase() }))} value={f.member_keys} onToggle={toggle("member_keys")} testid="event-member" /></div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Repeat" htmlFor="ev-rep"><select id="ev-rep" className="q-input" value={f.freq} onChange={set("freq")} data-testid="event-repeat-select">
              <option value="">Doesn't repeat</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></Field>
            <Field label="Reminder" htmlFor="ev-rem"><select id="ev-rem" className="q-input" value={f.reminder} onChange={set("reminder")} data-testid="event-reminder-select">
              <option value="">None</option><option value="15m">15 minutes before</option><option value="1h">1 hour before</option><option value="1d">1 day before</option></select></Field>
          </div>
          {f.freq === "weekly" && <Chips options={DAYS.map((d, i) => ({ value: i, label: d, tid: d.toLowerCase() }))} value={f.days} onToggle={toggle("days")} testid="event-repeat-day" />}
          {f.freq && <Field label="Ends on (optional)" htmlFor="ev-until"><input id="ev-until" type="date" className="q-input" value={f.until} onChange={set("until")} data-testid="event-repeat-until" /></Field>}
          <Field label="Notes" htmlFor="ev-notes"><textarea id="ev-notes" className="q-input min-h-[72px]" value={f.notes} onChange={set("notes")} data-testid="event-notes-input" /></Field>
          {askScope ? <ScopeAsk verb="Save changes to" busy={busy} onPick={save} onCancel={() => setAskScope(false)} /> : (
            <div className="flex justify-end gap-2">
              <button type="button" className="q-btn q-btn-secondary" onClick={onClose} data-testid="event-cancel-btn">Cancel</button>
              <button className="q-btn q-btn-primary" disabled={busy} data-testid="event-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ScopeAsk({ verb, busy, onPick, onCancel }) {
  return (
    <div className="rounded-xl border border-q-line p-4" data-testid="event-scope-ask">
      <p className="mb-3 text-sm font-semibold text-q-text">{verb} this repeating event:</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="q-btn q-btn-primary" disabled={busy} onClick={() => onPick("this")} data-testid="event-scope-this">This event</button>
        <button type="button" className="q-btn q-btn-secondary" disabled={busy} onClick={() => onPick("all")} data-testid="event-scope-all">All events in the series</button>
        <button type="button" className="q-btn q-btn-ghost" onClick={onCancel} data-testid="event-scope-cancel">Cancel</button>
      </div>
    </div>
  );
}

export function EventDetails({ hubId, meta, e, onClose, onEdit, onChanged }) {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const names = Object.fromEntries(meta.people.map((p) => [p.key, p.name]));
  const del = async (scope) => {
    if (e.repeat && !scope) return setAsk(true);
    setBusy(true);
    try {
      await api.delete(`/organizations/${hubId}/calendar/events/${e.event_id}`, { params: { scope: scope || "all", occurrence: e.occurrence } });
      toast.success("Event deleted");
      onChanged();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const rgb = CAT_RGB[e.category];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="event-details">
        <DialogHeader><DialogTitle data-testid="event-details-title">{e.title}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <span className="inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-black uppercase tracking-[0.12em]" style={{ background: `rgba(${rgb},0.16)`, color: `rgb(${rgb})` }} data-testid="event-details-category">{e.category}</span>
          <p className="text-q-text" data-testid="event-details-when">{new Date(e.all_day ? `${e.start}T12:00` : e.start).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })} · {whenLabel(e)}</p>
          {e.location && <p className="flex items-center gap-1.5 text-q-muted" data-testid="event-details-location"><MapPin className="h-4 w-4" />{e.location}</p>}
          {!!e.member_keys.length && <p className="text-q-muted" data-testid="event-details-members">{e.member_keys.map((k) => names[k]).filter(Boolean).join(", ")}</p>}
          {e.repeat && <p className="text-q-faint" data-testid="event-details-repeat">Repeats {e.repeat.freq}{e.repeat.until ? ` until ${e.repeat.until}` : ""}</p>}
          {e.reminder && <p className="text-q-faint">Reminder {e.reminder === "1d" ? "1 day" : e.reminder === "1h" ? "1 hour" : "15 minutes"} before</p>}
          {e.notes && <p className="whitespace-pre-wrap text-q-muted">{e.notes}</p>}
          {e.source === "link" && <p className="flex items-center gap-1.5 rounded-lg bg-q-raised px-3 py-2 font-semibold text-q-muted" data-testid="event-details-readonly"><Link2 className="h-4 w-4" /> Edit this in {e.source_name}</p>}
          {e.source === "flight" && <p className="text-xs text-q-faint">Added from Flights & Trips.</p>}
        </div>
        {meta.can_edit && e.source !== "link" && (ask ? <ScopeAsk verb="Delete" busy={busy} onPick={del} onCancel={() => setAsk(false)} /> : (
          <div className="mt-2 flex justify-end gap-2">
            <button className="q-btn q-btn-secondary" disabled={busy} onClick={() => del()} data-testid="event-delete-btn"><Trash2 className="h-4 w-4" /> Delete</button>
            {e.source !== "flight" && <button className="q-btn q-btn-primary" onClick={onEdit} data-testid="event-edit-btn"><Pencil className="h-4 w-4" /> Edit</button>}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
